import { define } from 'custom-elements';

/**
 * The sticky add bar on a phone.
 *
 * Shows once the page's own add button has scrolled up out of sight, and hides
 * again when it comes back. It never adds anything itself: its button presses
 * the real one, so the product form and the kit builder keep sole charge of
 * what goes in the cart, and the bar only mirrors their label and price.
 *
 * "The real one" is whichever add button is actually on screen. A case page
 * carries two (the case form's and the kit builder's) and the purchase switch
 * hides one of them with `display: none` or `hidden`, so the visible one is the
 * one with a layout box. The choice is re-made on scroll and whenever the
 * switch changes `data-purchase-mode` on <html>.
 */
class StickyAdd extends HTMLElement {
  static SOURCES = '.product [data-add-button], kit-builder [data-kit-add]';

  connectedCallback() {
    this.button = this.querySelector('[data-sticky-button]');
    this.label = this.querySelector('[data-sticky-label]');
    this.price = this.querySelector('[data-sticky-price]');
    this.source = null;

    this.button?.addEventListener('click', () => this.press());

    // One measurement per frame, however fast the scroll events come.
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };

    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.schedule);

    // The real button's label and price change under it: a variant picked, a
    // kit step completed, "Adding…" and "Added". Copy them across as they do.
    this.mirror = new MutationObserver(() => this.sync());

    this.modeWatch = new MutationObserver(this.schedule);
    this.modeWatch.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-purchase-mode'],
    });

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.schedule);
    window.removeEventListener('resize', this.schedule);
    cancelAnimationFrame(this.frame);
    this.mirror?.disconnect();
    this.modeWatch?.disconnect();
  }

  /** The add button currently laid out on the page, if any. */
  findSource() {
    return [...document.querySelectorAll(StickyAdd.SOURCES)].find((node) => node.getClientRects().length > 0) || null;
  }

  /**
   * The line the real button has to clear before the bar appears: the bottom
   * of the sticky header while it is showing, or the top of the screen once it
   * has slid away. A button tucked behind the header is out of sight too.
   */
  get topEdge() {
    const header = document.querySelector('.header-section');
    return Math.max(0, header?.getBoundingClientRect().bottom || 0);
  }

  update() {
    const source = this.findSource();

    if (source !== this.source) {
      this.mirror.disconnect();
      this.source = source;
      if (source) {
        this.mirror.observe(source, {
          attributes: true,
          attributeFilter: ['disabled', 'data-state'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
      this.sync();
    }

    const passed = Boolean(source) && source.getBoundingClientRect().bottom < this.topEdge;
    this.classList.toggle('is-visible', passed);
    // Off screen means out of the tab order and the accessibility tree too.
    this.inert = !passed;
  }

  /** The face the real button is showing right now, as plain text. */
  sourceLabel() {
    const source = this.source;
    const face = {
      adding: '.buy-buttons__label-adding',
      added: '.buy-buttons__label-added',
      alt: '.buy-buttons__label-alt',
    }[source.dataset.state] || '.buy-buttons__label-idle';

    const node = source.querySelector(face);
    // The idle label may have been split into two copies for the hover roll.
    const copy = node?.querySelector('.text-roll__text') || node;
    return copy?.textContent.trim() || '';
  }

  sync() {
    if (!this.source) return;

    const label = this.sourceLabel();
    if (label && this.label) this.label.textContent = label;

    const price = this.source.querySelector('.buy-buttons__price');
    if (this.price) {
      this.price.innerHTML = price ? price.innerHTML : '';
      this.price.hidden = !price;
    }

    this.button.dataset.state = this.source.dataset.state || 'idle';
    this.button.setAttribute('aria-disabled', String(this.source.dataset.state === 'adding'));
  }

  /**
   * Presses the real button. One that is waiting on a choice (a kit step not
   * yet made, a sold-out colour) is disabled, and pressing it would do
   * nothing, so the bar takes the customer back up to the choices instead;
   * the label already says which one is missing.
   */
  press() {
    const source = this.source;
    if (!source || source.dataset.state === 'adding') return;

    if (source.disabled) {
      const choices =
        source.closest('kit-builder')?.querySelector('.kit__form') ||
        source.closest('form')?.querySelector('variant-picker') ||
        source;
      choices.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    source.click();
  }
}

define('sticky-add', StickyAdd);
